import React from "react";
import { useParams, Link } from "react-router-dom";
import { useFetch } from "@/lib/useFetch";
import { usePageMeta, breadcrumbLd } from "@/lib/usePageMeta";
import { JsonLd } from "@/components/JsonLd";
import { useI18n } from "@/contexts/I18nContext";
import { NotFoundState, ErrorState } from "@/components/StateViews";
import { PriceComparisonPanel } from "@/components/PriceComparisonPanel";
import { StarRating } from "@/components/StarRating";
import { BookCover } from "@/components/BookCover";
import { ReadMore } from "@/components/ReadMore";
import { ReviewSection } from "@/components/ReviewSection";
import { RelatedDiscussions } from "@/components/RelatedDiscussions";
import { AdSlot } from "@/components/AdSlot";

function BookSkeleton() {
  // Same geometry as the loaded page, so content does not jump when it arrives.
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 lg:grid lg:grid-cols-[220px_1fr_300px] lg:gap-10">
      <div className="flex gap-4 lg:block">
        <div className="w-[34%] max-w-[150px] shrink-0 lg:w-full lg:max-w-none"><div className="aspect-[2/3] skel" /></div>
        <div className="flex-1 space-y-3 lg:hidden"><div className="skel h-8 w-4/5" /><div className="skel h-5 w-1/2" /></div>
      </div>
      <div className="mt-6 space-y-3 lg:mt-0"><div className="skel hidden h-12 w-3/4 lg:block" /><div className="skel h-32" /></div>
    </div>
  );
}

const Colophon = ({ book, t, lang }) => {
  const rows = [
    book.authors?.length > 0 && [t("written_by"), book.authors.map((a, i) => (
      <span key={a.id}>
        <Link to={`/authors/${a.slug}`} className="text-primary hover:underline underline-offset-4" data-testid={`book-author-link-${a.slug}`}>
          {lang === "hi" ? a.name_hi : (a.name_en || a.name_hi)}
        </Link>{i < book.authors.length - 1 ? ", " : ""}
      </span>
    ))],
    book.publisher && [t("published_by"), (
      <Link to={`/publishers/${book.publisher.slug}`} className="text-primary hover:underline underline-offset-4" data-testid={`book-publisher-link-${book.publisher.slug}`}>{book.publisher.name}</Link>
    )],
    book.publication_year && [t("year"), book.publication_year],
    book.page_count && [t("pages"), book.page_count],
    book.genres?.length > 0 && [t("genre"), book.genres.map((g, i) => (
      <span key={g}>
        <Link to={`/books?genre=${encodeURIComponent(g)}`} className="hover:text-primary">{g}</Link>{i < book.genres.length - 1 ? ", " : ""}
      </span>
    ))],
  ].filter(Boolean);
  if (!rows.length) return null;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-6 border-y border-border py-3 text-[0.97rem]" data-testid="book-colophon">
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt className="py-1 text-muted-foreground">{label}</dt>
          <dd className="py-1 min-w-0 break-words">{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

export default function BookDetail() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const { status, data: book, reload } = useFetch(`/books/${encodeURIComponent(slug)}`);
  usePageMeta({
    title: book ? (lang === "hi" ? book.title_hi : (book.title_en || book.title_hi)) : status === "notfound" ? t("not_found_title") : undefined,
    description: book?.synopsis, type: "book", image: book?.cover_image || undefined,
    noindex: status === "notfound" || status === "error",
  });

  if (status === "notfound") return <NotFoundState />;
  if (status === "error") return <ErrorState onRetry={reload} />;
  if (!book) return <BookSkeleton />;

  const title = lang === "hi" ? book.title_hi : (book.title_en || book.title_hi);
  const subtitle = lang === "hi" ? book.title_en : (book.title_en ? book.title_hi : "");
  const authorNames = (book.authors || []).map((a) => (lang === "hi" ? a.name_hi : a.name_en || a.name_hi)).join(", ");
  const rated = (book.review_count || 0) > 0;

  return (
    <article className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10">
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "Book", name: book.title_hi,
        ...(book.title_en ? { alternateName: book.title_en } : {}),
        ...(book.cover_image ? { image: book.cover_image } : {}),
        author: book.authors?.map((a) => ({ "@type": "Person", name: a.name_hi })),
        ...(book.publisher ? { publisher: { "@type": "Organization", name: book.publisher.name } } : {}),
        ...(book.page_count ? { numberOfPages: book.page_count } : {}),
        ...(book.publication_year ? { datePublished: String(book.publication_year) } : {}),
        ...(book.genres?.length ? { genre: book.genres } : {}),
        inLanguage: "hi",
        ...(rated ? { aggregateRating: { "@type": "AggregateRating", ratingValue: book.avg_rating, reviewCount: book.review_count } } : {}),
      }} />
      <JsonLd data={breadcrumbLd([{ name: t("site_name"), path: "/" }, { name: t("all_books"), path: "/books" }, { name: book.title_hi, path: `/books/${book.slug}` }])} />

      {/* One header and one price panel, placed by grid area: on phones the header
          sits beside a small cover and prices follow; on desktop the cover and the
          price rail span the height of the reading column. */}
      <div className="grid grid-cols-[34%_1fr] gap-x-4 gap-y-5 [grid-template-areas:'cover_head'_'price_price'_'main_main']
        sm:grid-cols-[150px_1fr] lg:grid-cols-[220px_1fr_300px] lg:gap-x-10 lg:gap-y-6 lg:[grid-template-areas:'cover_head_price'_'cover_main_price']">
        <div className="[grid-area:cover] min-w-0">
          <div className="lg:sticky lg:top-24">
            <BookCover src={book.cover_image} title={title} eager sizes="(max-width: 1024px) 34vw, 220px"
              className="rounded-[var(--radius)] shadow-[0_10px_24px_-12px_hsl(var(--foreground)/0.55)]" />
          </div>
        </div>

        <header className="[grid-area:head] min-w-0 lg:shirorekha lg:pb-4">
          <h1 className="text-[1.55rem] leading-[1.3] sm:text-[2rem] lg:text-[2.6rem] lg:leading-[1.25] break-words" data-testid="book-title">{book.title_hi}</h1>
          {subtitle && <p className="mt-1 text-muted-foreground lg:text-lg break-words">{subtitle}</p>}
          {authorNames && <p className="mt-2 break-words lg:hidden">{authorNames}</p>}
          {rated && (
            <p className="mt-2 flex flex-wrap items-center gap-x-2 text-sm lg:mt-3 lg:text-base">
              <StarRating value={book.avg_rating} size={16} />
              <span className="font-semibold">{Number(book.avg_rating).toFixed(1)}</span>
              <a href="#reviews" className="text-muted-foreground underline-offset-4 hover:underline">({book.review_count} {t("reviews")})</a>
            </p>
          )}
        </header>

        {book.prices?.length > 0 && (
          <aside className="[grid-area:price] min-w-0">
            {/* Not sticky: a sticky rail would ride alongside the ad unit in the main column. */}
            <PriceComparisonPanel bookId={book.id} prices={book.prices} />
          </aside>
        )}

        <div className="[grid-area:main] min-w-0">
          <div className="space-y-6">
            <ReadMore text={book.synopsis} lines={7} testid="book-synopsis" />
            <Colophon book={book} t={t} lang={lang} />
          </div>
          {/* After the book's own content, before reviews; never beside the price panel. */}
          <AdSlot placement="book-after-content" className="mt-10" />
          <div id="reviews" className="mt-12 scroll-mt-24"><ReviewSection targetType="book" targetId={book.id} /></div>
          <div className="mt-12"><RelatedDiscussions targetType="book" target={book} /></div>
        </div>
      </div>
    </article>
  );
}
